import { CropRegion, LayoutSegmentSlot } from '@sparemin/api-sdk';
import { compose } from 'redux';
import _, { partial } from 'underscore';
import { ScenePreset } from 'types';
import {
  createCaptionsOverrideState,
  createSlotsForPreset,
  CropSubjectOptions,
  fillSlots,
  normalizeAPISlots,
  selectPreset,
} from '../utils';
import { EditSceneModalAction, EditSceneModalState } from './types';

const DEFAULT_ENABLE_BLUR = false;

export const DEFAULT_STATE: EditSceneModalState = {
  captionsOverride: undefined,
  controlPanelMode: 'default',
  defaultCaptionsOverride: undefined,
  draftCrop: undefined,
  defaultEnableBlur: DEFAULT_ENABLE_BLUR,
  defaultSlots: undefined,
  enableBlur: DEFAULT_ENABLE_BLUR,
  isFrameSeeking: false,
  isPreviewReady: false,
  isSaving: false,
  slotCount: undefined,
  selectedPreset: ScenePreset.CURRENT,
  selectedSlotIndex: -1,
  slots: undefined,
  previewDimensions: undefined,
};

export default function reducer(
  state: EditSceneModalState = DEFAULT_STATE,
  action: EditSceneModalAction,
): EditSceneModalState {
  switch (action.type) {
    case 'open': {
      const {
        backfill,
        captionsOverride,
        defaultSlots,
        videoDimensions,
      } = action.payload;

      const normalizedSlots = normalizeAPISlots(defaultSlots, videoDimensions);
      const initialCaptionsOverride = createCaptionsOverrideState(
        captionsOverride,
      );

      return {
        ...DEFAULT_STATE,
        ...initialCaptionsOverride,
        defaultCaptionsOverride: initialCaptionsOverride?.captionsOverride,
        defaultEnableBlur: backfill?.enableBlur ?? DEFAULT_ENABLE_BLUR,
        defaultSlots: normalizedSlots,
        enableBlur: backfill?.enableBlur ?? DEFAULT_ENABLE_BLUR,
        slotCount: normalizedSlots.length,
        slots: normalizedSlots,
      };
    }

    case 'exit': {
      return DEFAULT_STATE;
    }

    case 'preview-resize': {
      const { width, height } = action.payload;
      const {
        previewDimensions: { width: currentWidth, height: currentHeight } = {},
      } = state;

      if (currentWidth === width && currentHeight === height) {
        return state;
      }

      return {
        ...state,
        previewDimensions: { width, height },
      };
    }

    case 'preview-canplay': {
      return {
        ...state,
        isPreviewReady: true,
      };
    }

    case 'select-preset': {
      const {
        captionsOverride,
        preset,
        subjects,
        videoDimensions,
      } = action.payload;
      const {
        defaultSlots,
        previewDimensions,
        selectedSlotIndex,
        slots,
      } = state;

      const captionsOverrideState =
        preset === ScenePreset.CURRENT
          ? createCaptionsOverrideState(captionsOverride)
          : createCaptionsOverrideState(undefined);

      // a function that when passed a list of new slots, will set these new
      // slots in state and return the new state.  this basically pre-fills
      // the state and preset properties of the selectPreset function so that
      // they don't have to be used in multiple function calls below
      const updatePresetSlots = (newSlots: LayoutSegmentSlot[]) => ({
        ...selectPreset(state, preset, newSlots),
        ...captionsOverrideState,
      });

      if (preset === ScenePreset.CURRENT) {
        updatePresetSlots(defaultSlots);
      }

      const emptySlots = createSlotsForPreset(
        preset,
        previewDimensions,
        videoDimensions,
      );

      // a function that will take a list of subjects and an anchor point and will
      // crop the subjects to fit into the slots for the given preset
      const fillPresetSlots = compose(
        updatePresetSlots,
        partial(fillSlots, emptySlots, _, previewDimensions, videoDimensions),
      ) as (
        subjects: CropRegion[],
        opts?: CropSubjectOptions,
      ) => EditSceneModalState;

      // if the user selects "full screen" while a slot is selected, assume that they
      // want the selected slot to go full screen.  take the crop from that slot and
      // fit it into the new slot
      if (
        preset === ScenePreset.FULL_SCREEN &&
        selectedSlotIndex >= 0 &&
        slots?.[selectedSlotIndex]?.staticCrop
      ) {
        const { staticCrop } = slots[selectedSlotIndex];

        // we don't know what the user has put in this slot, so use a "center"
        // anchor for now
        return fillPresetSlots([staticCrop], {
          anchor: { x: 'center', y: 'center' },
        });
      }

      // in these slots, we are inserting subjects we've found, which are typically
      // portraits, so use a "center" / "top" anchor to hopefully get a decent
      // fit without too much user-cropping
      return fillPresetSlots(subjects, {
        anchor: { x: 'center', y: 'top' },
      });
    }

    case 'toggle-blur': {
      const { isActive } = action.payload;

      return {
        ...state,
        enableBlur: isActive,
      };
    }

    case 'select-slot': {
      const { slotIndex } = action.payload;

      return {
        ...state,
        selectedSlotIndex: slotIndex,
      };
    }

    case 'adjust-crop-enter': {
      const { selectedSlotIndex, slots } = state;

      const initialDraftCrop = slots?.[selectedSlotIndex]?.staticCrop;

      return {
        ...state,
        controlPanelMode: 'adjust-crop',
        draftCrop: initialDraftCrop,
      };
    }

    case 'adjust-crop-update': {
      const { crop } = action.payload;

      return {
        ...state,
        draftCrop: crop,
      };
    }

    case 'adjust-crop-cancel': {
      return {
        ...state,
        controlPanelMode: 'default',
        draftCrop: undefined,
      };
    }

    case 'adjust-crop-commit': {
      const { draftCrop, selectedSlotIndex, slots } = state;

      const newSlots = [...slots];

      newSlots[selectedSlotIndex] = {
        ...newSlots[selectedSlotIndex],
        staticCrop: draftCrop,
      };

      return {
        ...state,
        slots: newSlots,
        controlPanelMode: 'default',
        draftCrop: undefined,
      };
    }

    case 'swap-slots': {
      const { sourceIndex, targetIndex } = action.payload;
      const { slots } = state;

      if (!slots || sourceIndex === targetIndex) {
        return state;
      }

      const sourceSlot = slots[sourceIndex];
      const targetSlot = slots[targetIndex];

      const sourceCrop = slots[sourceIndex]?.staticCrop;
      const targetCrop = slots[targetIndex]?.staticCrop;

      if (!sourceSlot || !targetSlot || !sourceCrop || !targetCrop) {
        return state;
      }

      const newSlots = [...slots];

      newSlots[sourceIndex] = {
        ...sourceSlot,
        staticCrop: targetCrop,
      };

      newSlots[targetIndex] = {
        ...targetSlot,
        staticCrop: sourceCrop,
      };

      return {
        ...state,
        slots: newSlots,
        // this is a cheap way to make sure the "same slot" stays selected when
        // the user "drops" (e.g. if slot 1 was dragged and dropped onto slot 2,
        // then after the drop slot 2 should be selected since that used to be slot 1
        // and is what the user actually clicked on)
        selectedSlotIndex: targetIndex,
      };
    }

    case 'frame-seek-start': {
      return {
        ...state,
        isFrameSeeking: true,
      };
    }

    case 'frame-seek-end': {
      return {
        ...state,
        isFrameSeeking: false,
      };
    }

    case 'captions-drag-start': {
      return {
        ...state,
        selectedSlotIndex: -1,
      };
    }

    case 'captions-drag-stop': {
      const { position } = action.payload;

      return {
        ...state,
        captionsOverride: {
          ...(state.captionsOverride ?? {}),
          position,
        },
      };
    }

    case 'save-start': {
      return {
        ...state,
        isSaving: true,
      };
    }

    case 'save-end': {
      return {
        ...state,
        isSaving: false,
      };
    }

    default:
      return state;
  }
}
